/**
 * Arena — every surface the PvP flow puts on screen.
 *
 * Janeks' sequence, in order:
 *   1. press Arena           -> "Enter the arena?" with a preview of your
 *                               survivor and what you are carrying, Cancel/Join
 *   2. queued                -> "pending + timer" under the menus, and you keep
 *                               playing while it waits
 *   3. matched               -> "Match found — Enter Battle"
 *   4. accepted              -> loading screen while the map loads
 *   5. loaded                -> wait up to 30s for everyone to be in
 *   6. everyone in           -> 8-second countdown, nobody can move
 *   7. zero                  -> go
 *
 * ─── Two kinds of surface, and the difference matters ───────────────────────
 * The PENDING badge is not a panel. It mounts into `Hud.arenaStatusHost`, in
 * normal flow under the action bar, inside the one column that renders above
 * the full-screen panel scrims. That is what makes "you can still walk around
 * and do stuff" true in practice: the badge stays visible and its Cancel stays
 * clickable with the inventory, the map or a vendor open on top of everything.
 *
 * Everything else IS a modal, deliberately — an offer with a 20-second fuse
 * and a countdown you cannot act during are both moments where the game should
 * have your attention.
 *
 * ─── Nothing here counts down ───────────────────────────────────────────────
 * Every timer on screen is derived per frame from the last server view plus
 * elapsed wall time (`remainingMs`/`queuedForMs` in arena-state.ts). A
 * background tab gets its intervals throttled, so a counted-down number comes
 * back wrong while a derived one is simply correct.
 *
 * ─── The countdown reads ONE number ─────────────────────────────────────────
 * `ArenaStateView.msRemaining`, not the room's synced `freezeRemaining`. Both
 * describe the same 8 seconds, but `msRemaining` arrives with the seat in the
 * same push and needs no room connection, so it is available strictly earlier.
 * Showing both would mean two numbers disagreeing by the room-join latency.
 * The room still ENFORCES the freeze server-side; this is only the display.
 */
import { actionIconSvg } from './action-bar';
import { gameStateStore } from '../../state/game-state';
import { authStore } from '../../../core/state/auth-store';
import { playerDisplayName } from '../render/name-tags';
import {
  arenaStateStore, acceptMatch, closeArenaConfirm, countdownSeconds, declineMatch,
  formatDuration, joinArena, leaveArena, queuedForMs, remainingMs,
} from '../../state/arena-state';
import type { ArenaState } from '../../state/arena-state.types';
import { CharacterPreview } from './character-preview';
import {
  emptySlotHtml, gridSlotCount, slotGridStyle, slotHtml, SLOT_COLUMNS, SLOT_MIN_ROWS,
} from './item-slot';
import { pictureHtml } from './paperdoll';
import { arenaArtUrl, embersHtml, preloadImages } from './embers';

export interface ArenaUI {
  /** Mounted into `Hud.arenaStatusHost` — see the header. */
  readonly statusEl: HTMLElement;
  /** The modal layer, mounted over the canvas. */
  readonly modalEl: HTMLElement;
  /** Repaints the derived timers. Called from the frame loop; writes only on change. */
  update(): void;
  dispose(): void;
}

/** True while a modal arena surface owns the screen — used to gate other panels and movement keys. */
let modalOpen = false;
export function isArenaModalOpen(): boolean {
  return modalOpen;
}

export function createArenaUI(): ArenaUI {
  const statusEl = document.createElement('div');
  statusEl.className = 'bv-arena-status';
  statusEl.hidden = true;

  const modalEl = document.createElement('div');
  modalEl.className = 'bv-arena-modal';
  modalEl.hidden = true;

  const preview = new CharacterPreview();

  // ─── Pending badge ────────────────────────────────────────────────
  // Janeks, 2026-10-16: *"Style the Pending arena block. So that it is more
  // visual. Perhaps add a waiting animation icon. instead of 'X' cross, use
  // 'stop' label"*. The icon is the Arena button's own crossed blades inside
  // a turning ring — the same glyph that started the search, now visibly
  // searching. The ring is CSS, so reduced-motion can stop it.
  statusEl.innerHTML = `
    <div class="bv-arena-pending" role="status">
      <span class="bv-arena-seek" aria-hidden="true">
        <span class="bv-arena-seek-ring"></span>
        ${actionIconSvg('swords')}
      </span>
      <span class="bv-arena-pending-text">
        <span class="bv-arena-pending-label">Arena</span>
        <span class="bv-arena-pending-sub">Searching for an opponent<span class="bv-arena-ellipsis"><i>.</i><i>.</i><i>.</i></span></span>
        <span class="bv-arena-pending-timer" data-queue-timer>0:00</span>
      </span>
      <button type="button" class="bv-arena-cancel" data-leave title="Leave the queue">Stop</button>
    </div>`;
  (statusEl.querySelector('[data-leave]') as HTMLButtonElement).addEventListener('click', () => leaveArena());
  const queueTimerEl = statusEl.querySelector('[data-queue-timer]') as HTMLElement;

  // ─── Modal layer ──────────────────────────────────────────────────
  //
  // Janeks, 2026-10-16: *"Style properly the pre fight screen (accept fight),
  // add more game style to it"*. The scrim is now a stage: the painted pit
  // (Higgsfield, `public/ui/arena/versus.webp`) drifting slowly behind a
  // vignette, embers rising through it, and the card on top as a riveted
  // plate with a hazard band. `data-step` on the card picks the mood per
  // surface — the confirm keeps its preview layout untouched.
  modalEl.innerHTML = `
    <div class="bv-arena-scrim">
      <div class="bv-arena-backdrop" aria-hidden="true"></div>
      ${embersHtml(18)}
      <div class="bv-arena-card" role="dialog" aria-modal="true" aria-labelledby="bv-arena-title" data-step="confirm">
        <span class="bv-arena-band" aria-hidden="true"></span>
        <h2 class="bv-arena-title" id="bv-arena-title" data-title>Enter the arena?</h2>
        <p class="bv-arena-sub" data-sub></p>

        <div class="bv-arena-body">
          <div class="bv-arena-preview" data-preview></div>
          <div class="bv-arena-loadout" data-loadout></div>
        </div>

        <div class="bv-arena-vs" data-vs hidden></div>
        <div class="bv-arena-count" data-count hidden><span data-count-n>8</span></div>
        <div class="bv-arena-fuse" data-fuse hidden aria-hidden="true"><i data-fuse-fill></i></div>
        <p class="bv-arena-note" data-note></p>

        <div class="bv-arena-actions" data-actions></div>
      </div>
    </div>`;

  const titleEl = modalEl.querySelector('[data-title]') as HTMLElement;
  const subEl = modalEl.querySelector('[data-sub]') as HTMLElement;
  const bodyEl = modalEl.querySelector('.bv-arena-body') as HTMLElement;
  const previewHost = modalEl.querySelector('[data-preview]') as HTMLElement;
  const loadoutEl = modalEl.querySelector('[data-loadout]') as HTMLElement;
  const vsEl = modalEl.querySelector('[data-vs]') as HTMLElement;
  const countEl = modalEl.querySelector('[data-count]') as HTMLElement;
  const countNumEl = modalEl.querySelector('[data-count-n]') as HTMLElement;
  const noteEl = modalEl.querySelector('[data-note]') as HTMLElement;
  const actionsEl = modalEl.querySelector('[data-actions]') as HTMLElement;
  const cardEl = modalEl.querySelector('.bv-arena-card') as HTMLElement;
  const fuseEl = modalEl.querySelector('[data-fuse]') as HTMLElement;
  const fuseFillEl = modalEl.querySelector('[data-fuse-fill]') as HTMLElement;

  preview.mount(previewHost);

  /** What the modal is currently showing, so a repaint can skip a rebuild. */
  let renderedKey = '';
  /** The step the card last showed — a change replays the title's stamp-in. */
  let renderedStep = '';
  /**
   * The match the versus row was built for. The row is built ONCE per match
   * and only its pips are updated after that: its slide-in and the emblem's
   * slam are CSS animations, which re-run on every insertion, and the modal's
   * key changes on each side's accept and load — a rebuild there replayed the
   * whole entrance two or three times per match.
   */
  let versusFor = '';
  /** The offer's full fuse, captured from its first frame, so the bar can drain. */
  let fuseTotalMs = 0;

  // The pit is the first thing a match shows; fetch it while the player is
  // still queued rather than at the moment the offer lands.
  let versusPreloaded = false;

  function renderModal(state: ArenaState): void {
    const { view } = state;
    const show = state.confirmOpen
      || view.phase === 'PROPOSED' || view.phase === 'LOBBY' || view.phase === 'COUNTDOWN';

    modalEl.hidden = !show;
    modalOpen = show;

    // Deactivate BEFORE rendering, activate AFTER — the two halves are not
    // symmetric and cannot share one call site.
    //
    // Stopping early is free. Starting early is not: `setActive(true)` builds
    // the renderer and measures the host, and the host lives inside
    // `.bv-arena-body`, which the render functions below are what un-hide.
    // Activating first therefore measured a `display:none` box — 0x0, clamped
    // to 1x1 — every time the confirm was reopened after a match offer had
    // hidden it.
    if (!(show && state.confirmOpen)) preview.setActive(false);
    if (!show) {
      renderedKey = '';
      renderedStep = '';
      versusFor = '';
      fuseEl.hidden = true; // so the next offer captures its own full fuse
      if (view.phase === 'QUEUED' && !versusPreloaded) {
        versusPreloaded = true;
        preloadImages([arenaArtUrl('versus'), '/ui/arena/emblem.webp']);
      }
      return;
    }

    const step = state.confirmOpen ? 'confirm' : view.phase.toLowerCase();
    const key = `${step}|${view.match?.matchId ?? ''}`
      + `|${view.match?.opponent.loaded ?? ''}|${view.match?.you.loaded ?? ''}`
      + `|${view.match?.opponent.accepted ?? ''}|${view.match?.you.accepted ?? ''}`;
    if (key !== renderedKey) {
      renderedKey = key;
      cardEl.dataset.step = step;
      if (step !== renderedStep) {
        renderedStep = step;
        // Restart the stamp-in: removing the class and forcing a reflow is
        // what makes the animation run again (same trick as the countdown).
        titleEl.classList.remove('bv-arena-title-in');
        void titleEl.offsetWidth;
        titleEl.classList.add('bv-arena-title-in');
      }
      if (state.confirmOpen) renderConfirm();
      else if (view.phase === 'PROPOSED') renderProposed(state);
      else if (view.phase === 'LOBBY') renderLobby(state);
      else renderCountdown();
    }

    // Now the body is visible and laid out, so the preview measures a real box.
    if (state.confirmOpen) preview.setActive(true);
  }

  /** Step 1 — "enter the arena?", with the survivor and the bag they are taking in. */
  function renderConfirm(): void {
    const { character, equipment, inventory } = gameStateStore.get();

    titleEl.textContent = 'Enter the arena?';
    subEl.textContent = 'One opponent. One of you walks out.';
    bodyEl.hidden = false;
    vsEl.hidden = true;
    countEl.hidden = true;
    fuseEl.hidden = true;

    // The real slot names (game-state.types.ts's `EquipmentSlot`): PRIMARY /
    // SECONDARY are the two weapon hands, and the five armour pieces are
    // HEADGEAR / TORSO / LEGS / GLOVES / FEET. A duel is fought with whatever is in
    // PRIMARY, falling back to SECONDARY, and bare hands if neither.
    const weapon = equipment?.slots?.PRIMARY ?? equipment?.slots?.SECONDARY;
    const armorSlots = ['HEADGEAR', 'TORSO', 'VEST', 'LEGS', 'GLOVES', 'FEET'] as const;
    const wornCount = equipment ? armorSlots.filter((slot) => equipment.slots?.[slot]).length : 0;
    const carried = inventory?.carried ?? [];

    loadoutEl.innerHTML = `
      <div class="bv-arena-loadout-head">
        <span class="bv-arena-loadout-name">${esc(playerDisplayName(authStore.get().user))}</span>
        ${character?.state === 'ACTIVE' ? `<span class="bv-arena-loadout-lvl">Lv ${character.level}</span>` : ''}
      </div>
      <dl class="bv-arena-stats">
        <dt>Weapon</dt><dd>${weapon ? esc(weapon.def.name) + (weapon.broken ? ' <em>(broken)</em>' : '') : '<em>Bare hands</em>'}</dd>
        <dt>Armour</dt><dd>${equipment ? `${equipment.maxArmor} AR · ${wornCount}/${armorSlots.length} worn` : '—'}</dd>
        <dt>Health</dt><dd>${statHp()}</dd>
      </dl>
      <div class="bv-arena-bag-head">Carrying <span>${carried.length}</span></div>
      <div class="bv-slots bv-arena-bag" style="${slotGridStyle(SLOT_COLUMNS)}">${bagGrid(carried)}</div>`;

    // The one thing a player should know before pressing Join, and the only
    // consequence of the arena the rest of the game can see.
    noteEl.textContent = 'You fight with what you are carrying. Damage taken carries back out with you.';

    actionsEl.innerHTML = `
      <button type="button" class="bv-arena-btn bv-arena-btn-ghost" data-cancel>Cancel</button>
      <button type="button" class="bv-arena-btn bv-arena-btn-go" data-join>Join</button>`;
    bind('[data-cancel]', () => closeArenaConfirm());
    bind('[data-join]', () => joinArena());
  }

  /** Step 3 — an opponent was found and both sides have to commit. */
  function renderProposed(state: ArenaState): void {
    titleEl.textContent = 'Match found';
    subEl.textContent = 'Both fighters must accept.';
    bodyEl.hidden = true;
    countEl.hidden = true;
    vsEl.hidden = false;
    showVersus(state);
    // The fuse burns down from whatever was left when the offer first drew,
    // which is the full window unless the page was busy when it landed.
    if (fuseEl.hidden) fuseTotalMs = Math.max(1, remainingMs(state, Date.now()));
    fuseEl.hidden = false;
    noteEl.innerHTML = 'Accepting in <span data-deadline>—</span>';

    const youIn = state.view.match?.you.accepted ?? false;
    // The icon follows the label, never precedes it: the probes find this
    // button by text that starts with "Enter Battle", and the SVG is
    // aria-hidden with no title, so textContent is unchanged.
    actionsEl.innerHTML = `
      <button type="button" class="bv-arena-btn bv-arena-btn-ghost" data-decline>Decline</button>
      <button type="button" class="bv-arena-btn bv-arena-btn-go bv-arena-btn-fight${youIn ? ' is-done' : ''}" data-accept${youIn ? ' disabled' : ''}>Enter Battle${actionIconSvg('swords')}</button>`;
    bind('[data-decline]', () => declineMatch());
    bind('[data-accept]', () => acceptMatch());
  }

  /** Steps 4-5 — the loading screen, then waiting for the other side. */
  function renderLobby(state: ArenaState): void {
    const match = state.view.match!;
    const bothIn = match.you.loaded && match.opponent.loaded;

    titleEl.textContent = state.loadingMap ? 'Loading the arena' : 'Waiting for players';
    subEl.textContent = state.loadingMap
      ? 'Building the ground you are about to fight on.'
      : bothIn ? 'Everyone is in.' : 'Holding for the other fighter.';
    bodyEl.hidden = true;
    countEl.hidden = true;
    fuseEl.hidden = true;
    vsEl.hidden = false;
    showVersus(state);
    noteEl.innerHTML = state.loadingMap
      ? '<span class="bv-arena-spin" aria-hidden="true"></span> Loading…'
      : 'Starting in <span data-deadline>—</span> at the latest';
    // No way out of a lobby: both sides have committed, and a third button
    // here would be a free forfeit with no cost attached.
    actionsEl.innerHTML = '';
  }

  /** Step 6 — the freeze. */
  function renderCountdown(): void {
    titleEl.textContent = 'Stand by';
    subEl.textContent = 'Nobody moves until the count runs out.';
    bodyEl.hidden = true;
    vsEl.hidden = true;
    fuseEl.hidden = true;
    countEl.hidden = false;
    noteEl.textContent = '';
    actionsEl.innerHTML = '';
  }

  /** Builds the versus row once per match, then only moves its pips — see `versusFor`. */
  function showVersus(state: ArenaState): void {
    const m = state.view.match!;
    if (versusFor !== m.matchId) {
      versusFor = m.matchId;
      vsEl.innerHTML = versusHtml(state);
    }
    const pips = vsEl.querySelectorAll<HTMLElement>('.bv-arena-pip');
    paintPip(pips[0], m.you.accepted, m.you.loaded, state.view.phase);
    paintPip(pips[1], m.opponent.accepted, m.opponent.loaded, state.view.phase);
  }

  function versusHtml(state: ArenaState): string {
    const m = state.view.match!;
    return `
      <div class="bv-arena-side is-you">
        <span class="bv-arena-side-tag">You</span>
        <span class="bv-arena-side-name">${esc(m.you.username)}</span>
        <span class="bv-arena-side-lvl">Lv ${m.you.level}</span>
        <span class="bv-arena-pip"></span>
      </div>
      <div class="bv-arena-emblem" aria-hidden="true">
        <span class="bv-arena-emblem-flash"></span>
        <img src="/ui/arena/emblem.webp" alt="" width="120" height="120" decoding="async">
      </div>
      <span class="bv-arena-vs-word">vs</span>
      <div class="bv-arena-side is-them">
        <span class="bv-arena-side-tag">Opponent</span>
        <span class="bv-arena-side-name">${esc(m.opponent.username)}</span>
        <span class="bv-arena-side-lvl">Lv ${m.opponent.level}</span>
        <span class="bv-arena-pip"></span>
      </div>`;
  }

  function paintPip(pip: HTMLElement | undefined, accepted: boolean, loaded: boolean, phase: string): void {
    if (!pip) return;
    const done = phase === 'LOBBY' ? loaded : accepted;
    const label = phase === 'LOBBY'
      ? (loaded ? 'Ready' : 'Loading…')
      : (accepted ? 'Accepted' : 'Deciding…');
    if (pip.textContent !== label) pip.textContent = label;
    pip.classList.toggle('bv-arena-pip-on', done);
  }

  function statHp(): string {
    const { health } = gameStateStore.get();
    return health?.state === 'ACTIVE' ? `${Math.round(health.hp)} / ${health.maxHP}` : '—';
  }

  function bind(selector: string, fn: () => void): void {
    const el = actionsEl.querySelector(selector) as HTMLButtonElement | null;
    el?.addEventListener('click', fn);
  }

  // ─── Live repaint ─────────────────────────────────────────────────
  let lastQueueText = '';
  let lastDeadlineText = '';
  let lastCount = -1;

  function paintTimers(): void {
    const state = arenaStateStore.get();
    const now = Date.now();

    const queued = state.view.phase === 'QUEUED';
    statusEl.hidden = !queued;
    if (queued) {
      const text = formatDuration(queuedForMs(state, now));
      if (text !== lastQueueText) {
        lastQueueText = text;
        queueTimerEl.textContent = text;
      }
    }

    if (modalEl.hidden) return;

    const deadlineEl = noteEl.querySelector('[data-deadline]') as HTMLElement | null;
    if (deadlineEl) {
      const text = formatDuration(remainingMs(state, now));
      if (text !== lastDeadlineText) {
        lastDeadlineText = text;
        deadlineEl.textContent = text;
      }
    }

    if (!fuseEl.hidden) {
      const left = Math.max(0, Math.min(1, remainingMs(state, now) / fuseTotalMs));
      fuseFillEl.style.transform = `scaleX(${left.toFixed(3)})`;
      fuseEl.classList.toggle('is-low', left < 0.3);
    }

    if (!countEl.hidden) {
      const n = countdownSeconds(remainingMs(state, now));
      if (n !== lastCount) {
        lastCount = n;
        countNumEl.textContent = String(n);
        // Restart the pulse on each new digit. Removing the class and forcing
        // a reflow is what makes the animation re-run — re-adding it alone
        // does nothing if it was never removed.
        countEl.classList.remove('bv-arena-count-tick');
        void countEl.offsetWidth;
        countEl.classList.add('bv-arena-count-tick');
      }
    }
  }

  const unsub = arenaStateStore.subscribe((state) => {
    renderModal(state);
    paintTimers();
  });

  /**
   * Escape steps out of the arena OFFER, and only that one.
   *
   * Janeks: *"on ESC press - close the current modal."* Every other window
   * obeys that already; this one had no key handler at all.
   *
   * The arena modal is four surfaces behind one element, and only the first is
   * dismissible. Escaping a PROPOSED match would DECLINE it (a match offer has
   * a 20-second fuse and declining costs the other player their queue), and
   * LOBBY and COUNTDOWN are the deliberate "nobody can move" beats of the flow
   * — the room enforces both server-side, so a key that appeared to close them
   * would only be hiding a screen the player still cannot act on. Cancel is
   * available on the confirm step and nowhere else, which is exactly the rule
   * applied here.
   */
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' || !arenaStateStore.get().confirmOpen) return;
    e.preventDefault();
    closeArenaConfirm();
  };
  document.addEventListener('keydown', onKeyDown);

  return {
    statusEl,
    modalEl,
    update: paintTimers,
    dispose(): void {
      unsub();
      document.removeEventListener('keydown', onKeyDown);
      preview.dispose();
      statusEl.remove();
      modalEl.remove();
      modalOpen = false;
    },
  };
}

/**
 * The bag you are taking in, as the SAME slot grid the inventory draws — the
 * shared one from `ui/item-slot.ts`, at half the pitch (see `.bv-arena-bag` in
 * `styles/arena.css.ts`). Janeks: *"show a grid of items, same as inventory
 * only 2x smaller."*
 *
 * Two things this must not do, both of which it used to:
 *
 *  - **Resolve its own icon URL.** `iconPath` is a BARE FILENAME by contract
 *    (`inventory/item-catalog.coverage.test.ts`); the files are bundled and
 *    fingerprinted by Vite through `paperdoll.ts`'s `import.meta.glob` map.
 *    Interpolating it into `src` asks the server for `/bandage_roll_icon.webp`
 *    and gets a 404 — which is exactly what shipped. `pictureHtml` is the one
 *    resolver, and it returns '' for an unknown name rather than a broken
 *    image; `slotHtml` then falls back to the item's name.
 *  - **Truncate.** It capped at 12 with a "+N more" line. A bag you are about
 *    to fight with is the one place every item should be visible, and the grid
 *    grows by whole rows anyway.
 *
 * No actions and no `tipId`: `slotHtml`'s hover overlay and tooltip key are
 * inert without the owning panel's delegated click handler and an
 * `ItemTooltip`, and Use/Equip/Discard are meaningless inside a confirm.
 */
function bagGrid(items: ReadonlyArray<{ id: string; name: string; iconPath: string | null; quantity?: number }>): string {
  const total = gridSlotCount(items.length, SLOT_COLUMNS, SLOT_MIN_ROWS);
  const cells: string[] = [];
  for (let i = 0; i < total; i += 1) {
    const item = items[i];
    if (!item) {
      cells.push(emptySlotHtml());
      continue;
    }
    // The rendered box, not paperdoll's 78px: at 5 columns of 39px pitch with
    // 4px gaps a cell is ~36px, and the wrong hint makes the browser fetch the
    // 256w tier for it.
    const iconHtml = pictureHtml(item.iconPath, item.name, '', '36px');
    cells.push(slotHtml({
      itemId: item.id, name: item.name, iconPath: item.iconPath, quantity: item.quantity,
    }, iconHtml));
  }
  return cells.join('');
}

/** Everything interpolated into innerHTML here is server-supplied (usernames, item names). */
function esc(value: string): string {
  return value.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
}
